/**
 * useGraphEditor — 图谱编辑器 composable
 *
 * 封装编辑会话管理、diff 计算和 API 调用逻辑。
 * 在任意使用图谱的视图中引入此 composable，即可获得完整的编辑能力。
 *
 * 使用方式：
 * ```ts
 * const editor = useGraphEditor({
 *   onSaveSuccess: (stats) => { ElMessage.success(...) },
 *   onSaveError: (err) => { ElMessage.error(...) }
 * })
 *
 * // 进入编辑
 * editor.enterEditMode(nodes, edges)
 *
 * // 用户点击保存
 * await editor.save()
 *
 * // 用户取消
 * editor.cancelEdit()
 *
 * // 暴露给模板的响应式状态
 * editor.isEditing    // 是否处于编辑模式
 * editor.hasChanges   // 是否有未保存的变更
 * editor.isSaving     // 是否正在保存
 * ```
 */

import { ref, type Ref } from 'vue'
import type { GraphNode, GraphEdge } from '../@types'

export interface GraphEditorOptions {
  /**
   * 保存时合并进批量请求（如 file_source、userId、project），
   * 便于新增节点/边归属到某上传文件，Obsidian 导出可带上编辑结果。
   */
  batchExtra?: () => Partial<
    Pick<BatchUpdateRequest, 'file_source' | 'userId' | 'project'>
  >
  /** 保存成功后回调（参数为后端返回的统计信息） */
  onSaveSuccess?: (stats: {
    added_nodes: number
    added_edges: number
    updated_nodes: number
    updated_edges: number
    deleted_nodes: number
    deleted_edges: number
  }) => void
  /** 保存失败后回调 */
  onSaveError?: (error: Error) => void
  /** API 请求函数，默认使用 fetch */
  api?: {
    batchUpdate: (request: BatchUpdateRequest) => Promise<BatchUpdateResponse>
  }
}

export interface BatchUpdateRequest {
  add_nodes?: GraphNodeInput[]
  add_edges?: GraphEdgeInput[]
  update_nodes?: { id: string; [key: string]: any }[]
  update_edges?: { id: string; [key: string]: any }[]
  delete_node_ids?: string[]
  delete_edge_ids?: string[]
  userId?: string
  project?: string
  file_source?: string
}

export interface GraphNodeInput {
  id?: string
  label: string
  type?: string
  size?: number
  x?: number
  y?: number
  style?: Record<string, any>
}

export interface GraphEdgeInput {
  id?: string
  source: string
  target: string
  label?: string
  relation_type?: string
  style?: Record<string, any>
}

export interface BatchUpdateResponse {
  success: boolean
  message?: string
  stats?: {
    added_nodes: number
    added_edges: number
    updated_nodes: number
    updated_edges: number
    deleted_nodes: number
    deleted_edges: number
  }
}

/**
 * GraphVisualization 暴露给 editor 的接口
 */
export interface GraphVisualizationRef {
  getEditData: () => { nodes: GraphNode[]; edges: GraphEdge[] }
  applyEditData: (nodes: GraphNode[], edges: GraphEdge[]) => void
  promptAddNodeAtCenter: () => Promise<void>
  hintHowToAddRelation: () => void
}

export function useGraphEditor(options: GraphEditorOptions = {}) {
  // ========== 状态 ==========

  /** 当前是否为编辑模式 */
  const isEditing = ref(false)

  /** 是否正在保存 */
  const isSaving = ref(false)

  /** 是否有未保存的变更 */
  const hasChanges = ref(false)

  /** 原始数据（进入编辑时保存，取消时恢复） */
  const originalNodes = ref<GraphNode[]>([])
  const originalEdges = ref<GraphEdge[]>([])

  /** 当前编辑后的完整数据（实时更新） */
  const currentNodes = ref<GraphNode[]>([])
  const currentEdges = ref<GraphEdge[]>([])

  /** 图谱组件引用（通过 setGraphRef 注入） */
  let graphRef: GraphVisualizationRef | null = null

  // ========== 核心方法 ==========

  /**
   * 进入编辑模式
   * @param nodes 当前图谱节点
   * @param edges 当前图谱边
   */
  function enterEditMode(nodes: GraphNode[], edges: GraphEdge[]) {
    // 深拷贝保存原始数据
    originalNodes.value = JSON.parse(JSON.stringify(nodes))
    originalEdges.value = JSON.parse(JSON.stringify(edges))
    // 初始化当前数据
    currentNodes.value = JSON.parse(JSON.stringify(nodes))
    currentEdges.value = JSON.parse(JSON.stringify(edges))
    isEditing.value = true
    hasChanges.value = false
  }

  /**
   * 更新当前编辑数据（内部/外部调用）
   * 当图谱数据变化后，同步到 editor 内部
   */
  function updateCurrentData(nodes: GraphNode[], edges: GraphEdge[]) {
    currentNodes.value = JSON.parse(JSON.stringify(nodes))
    currentEdges.value = JSON.parse(JSON.stringify(edges))
  }

  function syncCurrentFromGraph() {
    if (graphRef) {
      const d = graphRef.getEditData()
      currentNodes.value = JSON.parse(JSON.stringify(d.nodes))
      currentEdges.value = JSON.parse(JSON.stringify(d.edges))
    }
  }

  function buildNodeUpdate(orig: GraphNode, cur: GraphNode): { id: string; [key: string]: any } | null {
    const fields = ['label', 'type', 'size', 'style', 'x', 'y'] as const
    const out: { id: string; [key: string]: any } = { id: cur.id }
    let changed = false
    for (const f of fields) {
      if (JSON.stringify(orig[f]) !== JSON.stringify(cur[f])) {
        out[f] = cur[f]
        changed = true
      }
    }
    return changed ? out : null
  }

  function buildEdgeUpdate(orig: GraphEdge, cur: GraphEdge): { id: string; [key: string]: any } | null {
    const fields = ['label', 'relation_type', 'source', 'target', 'style'] as const
    const out: { id: string; [key: string]: any } = { id: cur.id }
    let changed = false
    for (const f of fields) {
      if (JSON.stringify(orig[f]) !== JSON.stringify(cur[f])) {
        out[f] = cur[f]
        changed = true
      }
    }
    return changed ? out : null
  }

  /**
   * 计算变更差异（当前数据 vs 原始数据）
   * 返回可提交给后端的变更结构
   */
  function calculateDiff(): {
    addedNodes: GraphNode[]
    addedEdges: GraphEdge[]
    updatedNodes: { id: string; [key: string]: any }[]
    updatedEdges: { id: string; [key: string]: any }[]
    deletedNodeIds: string[]
    deletedEdgeIds: string[]
  } {
    syncCurrentFromGraph()

    const addedNodes = currentNodes.value.filter(
      (n) => !originalNodes.value.find((on) => on.id === n.id)
    )
    const deletedNodeIds = originalNodes.value
      .filter((n) => !currentNodes.value.find((cn) => cn.id === n.id))
      .map((n) => n.id)

    const addedEdges = currentEdges.value.filter(
      (e) => !originalEdges.value.find((oe) => oe.id === e.id)
    )
    const deletedEdgeIds = originalEdges.value
      .filter((e) => !currentEdges.value.find((ce) => ce.id === e.id))
      .map((e) => e.id)

    const updatedNodes: { id: string; [key: string]: any }[] = []
    for (const cur of currentNodes.value) {
      const orig = originalNodes.value.find((on) => on.id === cur.id)
      if (!orig || addedNodes.some((n) => n.id === cur.id)) continue
      const patch = buildNodeUpdate(orig, cur)
      if (patch) updatedNodes.push(patch)
    }

    const updatedEdges: { id: string; [key: string]: any }[] = []
    for (const cur of currentEdges.value) {
      const orig = originalEdges.value.find((oe) => oe.id === cur.id)
      if (!orig || addedEdges.some((e) => e.id === cur.id)) continue
      const patch = buildEdgeUpdate(orig, cur)
      if (patch) updatedEdges.push(patch)
    }

    return { addedNodes, addedEdges, updatedNodes, updatedEdges, deletedNodeIds, deletedEdgeIds }
  }

  /**
   * 检查是否有变更
   */
  function checkHasChanges(): boolean {
    const diff = calculateDiff()
    return (
      diff.addedNodes.length > 0 ||
      diff.addedEdges.length > 0 ||
      diff.updatedNodes.length > 0 ||
      diff.updatedEdges.length > 0 ||
      diff.deletedNodeIds.length > 0 ||
      diff.deletedEdgeIds.length > 0
    )
  }

  /**
   * 设置图谱组件引用（由 GraphVisualization 组件在挂载后调用，或由父组件注入）
   * 使得 save() / cancelEdit() 可以直接操作图谱
   */
  function setGraphRef(ref: GraphVisualizationRef | null) {
    graphRef = ref
  }

  /**
   * 获取图谱当前编辑数据（内部使用）
   */
  function getEditData(): { nodes: GraphNode[]; edges: GraphEdge[] } {
    if (graphRef) {
      return graphRef.getEditData()
    }
    return { nodes: currentNodes.value, edges: currentEdges.value }
  }

  /**
   * 标记有变更（由图谱可视化组件在增删改操作时调用）
   */
  function markChanged() {
    hasChanges.value = true
  }

  /**
   * 取消编辑 — 恢复原始数据
   * @param applyToGraph 是否同步恢复图谱组件（默认 true）
   */
  function cancelEdit(applyToGraph = true) {
    isEditing.value = false
    hasChanges.value = false
    const data = {
      nodes: JSON.parse(JSON.stringify(originalNodes.value)),
      edges: JSON.parse(JSON.stringify(originalEdges.value))
    }
    if (applyToGraph && graphRef) {
      graphRef.applyEditData(data.nodes, data.edges)
    }
    // 清空原始数据引用
    originalNodes.value = []
    originalEdges.value = []
    currentNodes.value = []
    currentEdges.value = []
    return data
  }

  /**
   * 保存编辑 — 计算 diff 并提交 API
   * @param applyToGraph 是否将结果同步到图谱组件（默认 true）
   */
  async function save(applyToGraph = true): Promise<{ nodes: GraphNode[]; edges: GraphEdge[] } | null> {
    if (isSaving.value) return null
    if (!graphRef) {
      console.warn('[useGraphEditor] save() called without a graphRef set. Call setGraphRef() first.')
      return null
    }
    isSaving.value = true

    try {
      const diff = calculateDiff()

      // 无变更
      if (
        diff.addedNodes.length === 0 &&
        diff.deletedNodeIds.length === 0 &&
        diff.addedEdges.length === 0 &&
        diff.deletedEdgeIds.length === 0 &&
        diff.updatedNodes.length === 0 &&
        diff.updatedEdges.length === 0
      ) {
        isEditing.value = false
        hasChanges.value = false
        isSaving.value = false
        return null
      }

      // 构造批量更新请求
      const request: BatchUpdateRequest = {
        add_nodes: diff.addedNodes.map((n) => ({
          id: n.id,
          label: n.label,
          type: n.type,
          size: n.size,
          x: n.x,
          y: n.y,
          style: n.style
        })),
        add_edges: diff.addedEdges.map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          label: e.label,
          relation_type: e.relation_type,
          style: e.style
        })),
        update_nodes: diff.updatedNodes,
        update_edges: diff.updatedEdges,
        delete_node_ids: diff.deletedNodeIds,
        delete_edge_ids: diff.deletedEdgeIds
      }

      const extra = options.batchExtra?.() ?? {}
      for (const [key, val] of Object.entries(extra)) {
        if (val === undefined || val === null) continue
        if (key === 'file_source' && String(val).trim() === '') continue
        ;(request as Record<string, unknown>)[key] = val
      }

      let response: BatchUpdateResponse

      if (options.api?.batchUpdate) {
        response = await options.api.batchUpdate(request)
      } else {
        const res = await fetch('/api/graph/batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request)
        })
        response = await res.json()
      }

      if (response.success && response.stats) {
        options.onSaveSuccess?.(response.stats)
        isEditing.value = false
        hasChanges.value = false
        const result = {
          nodes: JSON.parse(JSON.stringify(currentNodes.value)),
          edges: JSON.parse(JSON.stringify(currentEdges.value))
        }
        if (applyToGraph && graphRef) {
          graphRef.applyEditData(result.nodes, result.edges)
        }
        originalNodes.value = []
        originalEdges.value = []
        currentNodes.value = []
        currentEdges.value = []
        return result
      } else {
        throw new Error(response.message || '保存失败')
      }
    } catch (err: any) {
      options.onSaveError?.(err)
      return null
    } finally {
      isSaving.value = false
    }
  }

  return {
    // 状态
    isEditing: isEditing as Readonly<Ref<boolean>>,
    hasChanges: hasChanges as Readonly<Ref<boolean>>,
    isSaving: isSaving as Readonly<Ref<boolean>>,

    // 图谱组件引用（供外部如 toolbar 直接调用其实例方法）
    graphRef,

    // 方法
    setGraphRef,
    enterEditMode,
    updateCurrentData,
    calculateDiff,
    checkHasChanges,
    markChanged,
    cancelEdit,
    save,
    // 内部获取数据（供高级用法）
    getEditData
  }
}

export type GraphEditor = ReturnType<typeof useGraphEditor>
